import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { 
  getUserHighlights, 
  updateHighlightCover, 
  setCoverFromStory, 
  deleteHighlightGroup 
} from "../../functions/story";
import ProfileImage from "../../components/ProfileImage";
import StoryViewer from "../../components/stories/StoryViewer";
import "./style.css";

export default function Highlights({ username, token, visitor, profileId }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [highlights, setHighlights] = useState([]);
  const [highlightCovers, setHighlightCovers] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [showMenu, setShowMenu] = useState(null);
  const [showEditCoverModal, setShowEditCoverModal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [pendingCoverFile, setPendingCoverFile] = useState(null);
  const [pendingHighlightGroup, setPendingHighlightGroup] = useState(null);
  const [showCoverConfirm, setShowCoverConfirm] = useState(false);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);

  // Use provided username or current user's username
  const targetUsername = username || user?.username;
  const isOwnProfile = !visitor || targetUsername === user?.username;
  const targetUserId = profileId || (isOwnProfile ? user?.id : null);

  const fetchHighlights = useCallback(async () => {
    try {
      setLoading(true);
      // Pass targetUserId if viewing another profile, otherwise null (gets current user's highlights)
      const res = await getUserHighlights(targetUserId, token);
      if (res && res.highlights) {
        // Filter by username to ensure we only show highlights for the correct user
        const filteredHighlights = res.highlights.filter(
          (highlight) => highlight.user.username === targetUsername
        );
        
        // Store highlight covers
        if (res.highlightCovers) {
          setHighlightCovers(res.highlightCovers);
        }
        
        // Group highlights by highlightName (or use "Memories" as default display name)
        const groupedHighlights = {};
        filteredHighlights.forEach((highlight) => {
          const groupName = highlight.highlightName || "Memories";
          if (!groupedHighlights[groupName]) {
            groupedHighlights[groupName] = [];
          }
          groupedHighlights[groupName].push(highlight);
        });
        
        // Convert to array and sort each group by creation date (newest first)
        const groupedArray = Object.keys(groupedHighlights).map((name) => {
          const stories = groupedHighlights[name].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          return {
            name: name,
            stories: stories,
            // Use the first (most recent) story for thumbnail if no cover
            thumbnailStory: stories[0],
          };
        });
        
        setHighlights(groupedArray);
      }
    } catch (error) {
      console.error("Error fetching highlights:", error);
    } finally {
      setLoading(false);
    }
  }, [targetUserId, token, targetUsername]);

  useEffect(() => {
    fetchHighlights();
  }, [fetchHighlights]);

  const handleHighlightClick = (highlightGroup, index) => {
    // Format highlight group data to match StoryViewer expected format
    // All stories in the group should be from the same user
    const userStories = {
      user: highlightGroup.thumbnailStory.user,
      stories: highlightGroup.stories, // All stories in the group
    };
    setSelectedHighlight(userStories);
    setCurrentStoryIndex(0);
  };

  const handleCloseViewer = () => {
    setSelectedHighlight(null);
    setCurrentStoryIndex(0);
  };

  const handleNextStory = () => {
    // Navigate through stories within the selected highlight group
    if (selectedHighlight && currentStoryIndex < selectedHighlight.stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else {
      handleCloseViewer();
    }
  };

  const handlePrevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
    }
  };

  const getStoryThumbnail = (story) => {
    if (story.image) {
      return `/uploads/stories/${story.image}`;
    } else if (story.video) {
      return `/uploads/stories/${story.video}`;
    } else if (story.text) {
      // Text-only story - return null to show background color
      return null;
    }
    return null;
  };

  const getHighlightCover = (highlightName) => {
    // Check if there's a custom cover image
    if (highlightCovers[highlightName]) {
      return `/uploads/highlights/${highlightCovers[highlightName]}`;
    }
    return null;
  };

  const handleMenuClick = (e, highlightGroup) => {
    e.stopPropagation();
    e.preventDefault();
    const newMenuState = showMenu === highlightGroup.name ? null : highlightGroup.name;
    setShowMenu(newMenuState);
  };

  const handleEditCover = (highlightGroup) => {
    setShowMenu(null);
    setShowEditCoverModal(highlightGroup);
  };

  const handleDeleteHighlight = (highlightGroup) => {
    setShowMenu(null);
    setShowDeleteConfirm(highlightGroup);
  };

  const handleCoverSelect = (e, highlightGroup) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setCoverPreview(event.target.result);
      setPendingCoverFile(file);
      setPendingHighlightGroup(highlightGroup);
      setShowCoverConfirm(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCoverConfirm = async () => {
    if (!pendingCoverFile || !pendingHighlightGroup) return;

    try {
      setUploadingCover(true);
      setShowCoverConfirm(false);
      const formData = new FormData();
      formData.append("coverImage", pendingCoverFile);
      formData.append("highlightName", pendingHighlightGroup.name);

      const res = await updateHighlightCover(formData, token);
      if (res && res.highlight) {
        // Refresh highlights to get updated covers
        await fetchHighlights();
        setShowEditCoverModal(null);
        // Reset preview and pending file
        setCoverPreview(null);
        setPendingCoverFile(null);
        setPendingHighlightGroup(null);
      } else {
        console.error("Failed to upload cover:", res);
        alert("Failed to upload cover. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading cover:", error);
      alert("Failed to upload cover. Please try again.");
    } finally {
      setUploadingCover(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCoverCancel = () => {
    setShowCoverConfirm(false);
    setCoverPreview(null);
    setPendingCoverFile(null);
    setPendingHighlightGroup(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  
  const handleSetCoverFromStory = async (highlightGroup, storyId) => {
    try {
      setUploadingCover(true);
      const res = await setCoverFromStory(highlightGroup.name, storyId, token);
      if (res && res.highlight) {
        // Refresh highlights to get updated covers
        await fetchHighlights();
        setShowEditCoverModal(null);
      } else {
        console.error("Failed to set cover:", res);
        alert("Failed to set cover. Please try again.");
      }
    } catch (error) {
      console.error("Error setting cover from story:", error);
      alert("Failed to set cover. Please try again.");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleDeleteConfirm = async (highlightGroup) => {
    try {
      const res = await deleteHighlightGroup(highlightGroup.name, token);
      if (res && res.message === "Highlight group deleted successfully") {
        // Refresh highlights
        await fetchHighlights();
      } else {
        console.error("Failed to delete highlight:", res);
      }
    } catch (error) {
      console.error("Error deleting highlight group:", error);
      alert("Failed to delete memories. Please try again.");
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside menu and menu button
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        // Also check if it's not the menu button
        const menuButton = event.target.closest('.highlight_menu_btn');
        if (!menuButton) {
          setShowMenu(null);
        }
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  if (loading) {
    return (
      <div className="profile_card">
        <div className="profile_card_header">Memories</div>
        <div style={{ padding: "20px", textAlign: "center" }}>Loading memories...</div>
      </div>
    );
  }

  return (
    <div className="profile_card">
      <div className="profile_card_header">
        Memories
      </div>
      {highlights.length > 0 ? (
        <div className="highlights_circular_container">
          <div className="highlights_circular_wrapper">
            {highlights.map((highlightGroup, index) => {
              const thumbnailStory = highlightGroup.thumbnailStory;
              const coverImage = getHighlightCover(highlightGroup.name);
              
              return (
                <div 
                  className="highlight_circular_item" 
                  key={highlightGroup.name + index}
                  style={{ position: 'relative' }}
                >
                  <div
                    className="highlight_circular_cover"
                    onClick={() => handleHighlightClick(highlightGroup, index)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        !coverImage && !thumbnailStory.image && !thumbnailStory.video && thumbnailStory.backgroundColor
                          ? thumbnailStory.backgroundColor
                          : "#f0f2f5",
                    }}
                  >
                    {coverImage ? (
                      <ProfileImage
                        src={coverImage}
                        alt="Memories cover"
                        className="highlight_circular_image"
                      />
                    ) : getStoryThumbnail(thumbnailStory) ? (
                      thumbnailStory.image ? (
                        <ProfileImage
                          src={getStoryThumbnail(thumbnailStory)}
                          alt="Memories"
                          className="highlight_circular_image"
                        />
                      ) : (
                        <video
                          src={getStoryThumbnail(thumbnailStory)}
                          className="highlight_circular_video"
                          muted
                          onMouseEnter={(e) => e.target.play()}
                          onMouseLeave={(e) => {
                            e.target.pause();
                            e.target.currentTime = 0;
                          }}
                        />
                      )
                    ) : (
                      <div
                        className="highlight_circular_text"
                        style={{
                          fontFamily: thumbnailStory.font || "Arial",
                          color: "#fff",
                        }}
                      >
                        {thumbnailStory.text ? (thumbnailStory.text.length > 20 ? thumbnailStory.text.substring(0, 20) + "..." : thumbnailStory.text) : "Text"}
                      </div>
                    )}
                  </div>
                  {isOwnProfile && (
                    <button
                      className="highlight_menu_btn notranslate"
                      translate="no"
                      onClick={(e) => handleMenuClick(e, highlightGroup)}
                      title="Memories options"
                    >
                      <span className="notranslate">⋮</span>
                    </button>
                  )}
                  {isOwnProfile && showMenu === highlightGroup.name && (
                    <div 
                      className="highlight_menu" 
                      ref={menuRef}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCover(highlightGroup);
                        }}
                      >
                        <span className="notranslate">✏️</span> Edit Cover
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteHighlight(highlightGroup);
                        }}
                      >
                        <span className="notranslate">🗑</span> Delete
                      </button>
                    </div>
                  )}
                  <div className="highlight_circular_title">
                    <span className="notranslate">
                      {highlightGroup.name.length > 15 
                        ? highlightGroup.name.substring(0, 15) + "..." 
                        : highlightGroup.name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="no_highlights">
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#65676b",
              fontSize: "14px",
            }}
          >
            {isOwnProfile
              ? "You haven't saved any vibes to memories yet."
              : "This user hasn't saved any stories to memories yet."}
            <br />
            {isOwnProfile && (
              <span style={{ fontSize: "12px", marginTop: "10px", display: "block" }}>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Edit Cover Modal */}
      {showEditCoverModal && (
        <div className="edit_cover_modal_overlay" onClick={() => setShowEditCoverModal(null)}>
          <div className="edit_cover_modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit_cover_header">
              <h3>Edit Memories Cover</h3>
              <button onClick={() => setShowEditCoverModal(null)} className="close_btn">✕</button>
            </div>
            <div className="edit_cover_content">
              <h4>Select from existing stories:</h4>
              <div className="cover_stories_grid">
                {showEditCoverModal.stories
                  .filter((story) => story.image) // Only show image stories
                  .map((story) => (
                    <div
                      key={story._id}
                      className="cover_story_option"
                      onClick={() => handleSetCoverFromStory(showEditCoverModal, story._id)}
                    >
                      <ProfileImage
                        src={getStoryThumbnail(story)}
                        alt="Story"
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }}
                      />
                    </div>
                  ))}
              </div>
              
              <div className="edit_cover_upload">
                <h4>Or upload custom image:</h4>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleCoverSelect(e, showEditCoverModal)}
                  style={{ display: "none" }}
                />
                <button
                  className="upload_cover_btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingCover}
                >
                  {uploadingCover ? "Uploading..." : <><span className="notranslate">📷</span> Upload Cover Image</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cover Preview Confirmation Modal */}
      {showCoverConfirm && coverPreview && (
        <div className="cover_confirm_overlay" onClick={handleCoverCancel}>
          <div className="cover_confirm_modal" onClick={(e) => e.stopPropagation()}>
            <div className="cover_confirm_header">
              <h3>Confirm Cover Image</h3>
              <button onClick={handleCoverCancel} className="close_btn">✕</button>
            </div>
            <div className="cover_confirm_content">
              <p>Preview of your new cover image:</p>
              <div className="cover_preview_container">
                <img src={coverPreview} alt="Cover preview" className="cover_preview_image" />
              </div>
              <p className="cover_confirm_message">
                This will replace the current cover for "{pendingHighlightGroup?.name}" memories.
              </p>
              <div className="cover_confirm_actions">
                <button
                  className="cancel_btn"
                  onClick={handleCoverCancel}
                  disabled={uploadingCover}
                >
                  Cancel
                </button>
                <button
                  className="confirm_btn"
                  onClick={handleCoverConfirm}
                  disabled={uploadingCover}
                >
                  {uploadingCover ? "Uploading..." : "Confirm & Upload"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete_confirm_overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="delete_confirm_modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Memories?</h3>
            <p>Are you sure you want to delete these memories? All vibes in it will be removed.</p>
            <div className="delete_confirm_actions">
              <button
                className="cancel_btn"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="delete_btn"
                onClick={() => handleDeleteConfirm(showDeleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story Viewer for Memories */}
      {selectedHighlight && (
        <StoryViewer
          userStories={selectedHighlight}
          currentIndex={currentStoryIndex}
          onClose={handleCloseViewer}
          onNext={handleNextStory}
          onPrev={handlePrevStory}
          user={user}
          isHighlight={true}
          onStoryDeleted={() => {
            // Refresh highlights after deletion
            fetchHighlights();
          }}
        />
      )}
    </div>
  );
}

